export const FEA_VERTEX_SHADER = `
    #include <clipping_planes_pars_vertex>
    varying vec3 vWorldPosition;
    varying vec3 vNormal;
    varying vec3 vTexCoord;
    varying vec3 vUVW;  // New varying for interpolated texture coords

    uniform float dRmin;
    uniform float dRmax;

    uniform float dGmin;
    uniform float dGmax;

    uniform float dBmin;
    uniform float dBmax;

    uniform float dAmin;
    uniform float dAmax;

    uniform vec3 bboxMin;
    uniform vec3 bboxMax;

    // One voxel in UVW units per axis (1/grid dim) and how many voxels to inset the
    // colour-sampling clamp by, so boundary fragments read the interior value.
    uniform vec3 texelSize;
    uniform float sampleInset;

    uniform sampler3D texture3D;

    // View-space position (negated), for the camera-relative headlight.
    varying vec3 vViewPosition;

    uniform float uTime;
    uniform int animate;
    uniform int deformationType;
    uniform int vibration;
    uniform float deformScale;

    float scaleValue(float inMin, float inMax, float outMin, float outMax, float value) {
        return ((value - inMin) * (outMax - outMin)) / (inMax - inMin) + outMin;
    }

    void main() {
        #include <begin_vertex>
        #include <project_vertex>
        #include <clipping_planes_vertex>

        // Calculate UVW in vertex shader. Inset the clamp by ~sampleInset voxels per
        // axis so surface fragments at the domain boundary sample the valid interior
        // value instead of the zero-valued clamped/void edge voxel (removes the hard
        // blue edge line). Per-axis because the grid is anisotropic; capped at 0.45 so
        // a thin (few-voxel) axis just samples its mid-plane instead of inverting.
        // The bbox is in the mesh's own frame, so the lookup uses the vertex as
        // the mesh holds it and follows the mesh however it is placed.
        vec3 uvwInset = min(texelSize * sampleInset, vec3(0.45));
        vUVW = clamp((position - bboxMin) / (bboxMax - bboxMin), uvwInset, 1.0 - uvwInset);

        if(animate > 0) {
            vec3 bboxSize = bboxMax - bboxMin;
            vec3 normalizedPos = (position - bboxMin) / bboxSize;
            float bboxLen = length(bboxSize) * 0.03 * deformScale;
            float diagonal = length(vec3(dRmax - dRmin, dGmax - dGmin, dBmax - dBmin));

            float dX = dRmin + (texture(texture3D, normalizedPos).r * (dRmax - dRmin));
            float dY = dGmin + (texture(texture3D, normalizedPos).g * (dGmax - dGmin));
            float dZ = dBmin + (texture(texture3D, normalizedPos).b * (dBmax - dBmin));

            vec3 displacement;
            if(vibration == 1) {
                displacement = vec3(dX, dY, dZ) / diagonal * bboxLen * sin(uTime);
            } else {
                displacement = vec3(dX, dY, dZ) / diagonal * bboxLen * (1.0 + sin(uTime) * 0.5);
            }

            vec4 displacedWorldPosition = vec4(position + displacement, 1.0);

            vec4 worldPosition = modelMatrix * displacedWorldPosition;
            vWorldPosition = worldPosition.xyz;
            vNormal = normalize(normalMatrix * normal);
            vTexCoord = normalizedPos;

            vec4 displacedViewPosition = modelViewMatrix * displacedWorldPosition;
            vViewPosition = -displacedViewPosition.xyz;
            gl_Position = projectionMatrix * displacedViewPosition;
        } else {
            vec4 worldPosition = modelMatrix * vec4(position, 1.0);
            vWorldPosition = worldPosition.xyz;
            vNormal = normalize(normalMatrix * normal);
            vec4 viewPosition = modelViewMatrix * vec4(position, 1.0);
            vViewPosition = -viewPosition.xyz;
            gl_Position = projectionMatrix * viewPosition;
        }
    }
`;

export const FEA_FRAGMENT_SHADER = `
        precision highp float;
        #include <clipping_planes_pars_fragment>

        precision highp sampler3D;

        uniform sampler3D texture3D;
        uniform sampler3D texture3DStress;

        // Colour ramp baked into an RGBA8 sRGB lookup texture; sampling returns
        // linear colour.
        uniform sampler2D colormap;

        // Largest darkening from the key light falling away from a surface
        // (0 = unlit / no 3D shading at all). A face exactly facing the key
        // light shows the exact ramp colour; see AMBIENT_FLOOR = 1 - shadeContrast.
        uniform float shadeContrast;

        // How much the fill light (opposite the key) lifts the far side of a
        // part above AMBIENT_FLOOR, so it reads as gently lit rather than a
        // flat, textureless floor. Kept well below shadeContrast so the key
        // light — not the fill — is what reads as "the" light direction.
        uniform float fillStrength;

        varying vec3 vWorldPosition;
        varying vec3 vNormal;
        varying vec3 vTexCoord;
        varying vec3 vViewPosition;

        uniform int deformationType;

        // LUT range uniforms for custom min/max visualization
        uniform float lutMin;
        uniform float lutMax;

        varying vec3 vUVW;  // Now using interpolated coords

        void main() {
            #include <clipping_planes_fragment>

            // vUVW is already inset away from the domain boundary in the vertex shader,
            // so surface fragments read the valid interior value rather than the
            // zero-valued clamped/void edge voxel.
            vec3 uv = vUVW;
            float scalar = 0.0;
            if(deformationType == 4) {
                scalar = texture(texture3DStress, uv).r;
            } else if(deformationType == 3) {
                scalar = texture(texture3D, uv).b;
            } else if(deformationType == 2) {
                scalar = texture(texture3D, uv).g;
            } else if(deformationType == 1) {
                scalar = texture(texture3D, uv).r;
            } else if(deformationType == 0) {
                scalar = texture(texture3D, uv).a;
            }

            // Remap scalar to custom LUT range (lutMin to lutMax maps to 0-1)
            float remappedScalar = clamp((scalar - lutMin) / (lutMax - lutMin), 0.0, 1.0);

            // Texel-centre addressing: 0 and 1 read the centres of the first and
            // last texels, which hold the ramp's end colours.
            float lutSize = float(textureSize(colormap, 0).x);
            float lutU = (0.5 + remappedScalar * (lutSize - 1.0)) / lutSize;
            vec3 color = texture(colormap, vec2(lutU, 0.5)).rgb;

            // CAE-style contour shading: a camera-relative key light in the
            // same direction as the studio key light (Controls.createLightingRig's
            // (-4, 5, 6) offset — already in view space here, like vViewPosition),
            // a symmetric fill opposite it so the far side of a part never goes
            // flat, and an ambient floor (1 - shadeContrast). A face exactly
            // facing the key light lands at brightness 1.0 — the exact ramp
            // colour — matching the legend.
            //
            // Flat (per-triangle) normal via screen-space derivatives, not the
            // interpolated vNormal varying: this is the same formula three.js
            // uses for FLAT_SHADED (see normal_fragment_begin.glsl.js), so the
            // contour shading facets read identically to the restored
            // flatShading on the base mesh, instead of looking smooth on top
            // of a faceted surface.
            vec3 N = normalize(cross(dFdx(vViewPosition), dFdy(vViewPosition)));
            const vec3 KEY_LIGHT_DIR = normalize(vec3(-4.0, 5.0, 6.0));

            float keyDiffuse = max(dot(N, KEY_LIGHT_DIR), 0.0);
            float fillDiffuse = max(dot(N, -KEY_LIGHT_DIR), 0.0);
            float ambientFloor = 1.0 - shadeContrast;
            float brightness = ambientFloor + shadeContrast * keyDiffuse + fillStrength * fillDiffuse;

            // Scaling all channels in linear light changes brightness only,
            // never hue — a perceptual luminance scale, not a desaturating mix
            // toward grey.
            color *= brightness;

            // Very subtle, additive, near-white sheen along the key light's
            // reflection: a physically-motivated glint, not a hue shift. A
            // face exactly facing the key light is close to, but not exactly
            // at, this shader's specular peak (the view direction and key
            // light direction aren't the same), so it still picks up a small
            // contribution there — kept low enough (measured against the
            // darkest/most saturated legend colours, where a linear-light
            // addition shows up as the largest perceptual shift) that ΔE stays
            // under 3 at that reference orientation. A high shininess exponent
            // narrows the highlight so it still reads as a distinct glint on a
            // curved surface's true mirror angle elsewhere.
            const float SPECULAR_STRENGTH = 0.02;
            const float SPECULAR_SHININESS = 48.0;
            vec3 V = isOrthographic ? vec3(0.0, 0.0, 1.0) : normalize(vViewPosition);
            vec3 H = normalize(KEY_LIGHT_DIR + V);
            float spec = pow(max(dot(N, H), 0.0), SPECULAR_SHININESS) * SPECULAR_STRENGTH;
            color += spec;

            gl_FragColor = vec4(color, 1.0);
            #include <colorspace_fragment>
        }
    `;
